/**
 * Roof template slots.
 *
 * A template does not list products in a flat pile — it groups them by the part
 * of the roof they cover, and that grouping is what decides the quantity. Put a
 * product in "Eave products" and its quantity comes off the eave feet; put it
 * in "Ridge products" and it comes off the ridge. This mirrors how the company
 * already keeps its templates in ROOF.link, so the two stay legible to the
 * same person.
 *
 * Each section comes in a pair. The plain list is measurement-driven; the "ea"
 * list is one of each per job — a chimney reflash, a dump trailer, a turbine —
 * where the roof's size does not decide how many you need.
 */

import { DECK_SLOTS } from "./deckSlots.js";
import { WALL_SLOTS } from "./wallSlots.js";

/**
 * What drives a slot's quantity.
 *
 * Most are columns on `job_measurements`. Four are worked out from it: the two
 * steep-pitch bands add up the per-pitch squares, hip and ridge adds those two
 * lengths together, and the tear-off figure multiplies the roof by how many
 * layers are coming off. See `resolveMeasurement` in generator.ts.
 */
export type RoofSlotMeasurement =
  | "squares"
  | "eave_lf"
  | "rake_lf"
  | "ridge_lf"
  | "ridge_vent_lf"
  | "hip_lf"
  | "hip_ridge_lf"
  | "gutter_lf"
  | "valley_lf"
  | "flashing_lf"
  | "step_flashing_lf"
  | "transition_lf"
  | "flat_squares"
  | "flat_perimeter_lf"
  | "flat_membrane_squares"
  | "flat_insulation_squares"
  | "flat_deck_squares"
  | "open_edge_lf"
  | "wall_lf"
  | "steep_mid_squares"
  | "steep_high_squares"
  | "tearoff_squares"
  | "extra_layer_squares"
  /*
   * The wall, for a siding template. Worked out from `job_wall_measurements`
   * by `wallMeasurement` in siding.ts, which says what each one adds up.
   */
  | "siding_squares"
  | "siding_removal_squares"
  | "house_wrap_squares"
  | "soffit_squares"
  | "outside_corner_posts"
  | "inside_corner_posts"
  | "j_channel_lf"
  | "siding_starter_lf"
  | "openings_ea"
  | "soffit_lf"
  | "fascia_lf"
  | "frieze_lf";

/** What a slot is measured on. A template shows the slots of its own surface. */
export type SlotSurface = "roof" | "wall" | "deck";

export interface RoofSlot {
  key: string;
  label: string;
  /** Null means one of each, regardless of how big the roof is. */
  measurement: RoofSlotMeasurement | null;
  /** Roof unless said otherwise. */
  surface?: SlotSurface;
  /**
   * No waste on this section, whatever the layout or the product carries.
   *
   * A corner post is one per corner and a window wrap is one per window:
   * counts worked out by rule, where a percentage would buy a post for a
   * corner the house does not have. Removal is what is on the wall now, and
   * nobody tears off ten percent more than is there.
   */
  noWaste?: boolean;
}

const pair = (
  key: string,
  label: string,
  measurement: RoofSlotMeasurement,
): RoofSlot[] => [
  { key, label: `${label} products`, measurement },
  { key: `${key}_ea`, label: `${label} products ea`, measurement: null },
];

export const ROOF_SLOTS: readonly RoofSlot[] = [
  ...pair("field", "Field", "squares"),
  ...pair("eave", "Eave", "eave_lf"),
  ...pair("rake", "Rake", "rake_lf"),
  ...pair("ridge", "Ridge", "ridge_lf"),
  ...pair("ridge_vent", "Ridge vent", "ridge_vent_lf"),
  ...pair("hip", "Hip", "hip_lf"),
  // Ridge cap is bought against the two added together, not each rounded up on
  // its own. Forty feet of ridge and sixty of hip is five bundles of Seal A
  // Ridge, not the six you get from rounding twice.
  ...pair("hip_ridge", "Hip & ridge", "hip_ridge_lf"),
  ...pair("gutter", "Gutter", "gutter_lf"),
  ...pair("valley", "Valley", "valley_lf"),
  ...pair("flashing", "Flashing", "flashing_lf"),
  ...pair("step_flashing", "Step flashing", "step_flashing_lf"),
  ...pair("transition", "Transition", "transition_lf"),
  {
    key: "flat_field",
    label: "Flat area field products",
    measurement: "flat_squares",
  },
  /*
   * Three flat areas, because three different things are bought against them.
   *
   * `flat_field` above is the original and stays exactly what it was — the
   * measured flat area, nothing added. These three separate out what a TPO
   * roof actually needs:
   *
   *  - the membrane and the crew's rate cover the parapet walls as well as the
   *    roof, since the membrane runs up them and the crew is paid for it;
   *  - the insulation covers the roof, and the walls only when the rep said so;
   *  - the flute fill covers the deck alone, always — it fills the ribs of a
   *    metal deck, and a parapet has no deck.
   *
   * See `resolveMeasurement` for the arithmetic and the quotes behind it.
   */
  {
    key: "flat_membrane",
    label: "Flat membrane products (roof + walls)",
    measurement: "flat_membrane_squares",
  },
  {
    key: "flat_membrane_ea",
    label: "Flat membrane products ea",
    measurement: null,
  },
  {
    key: "flat_insulation",
    label: "Flat insulation products",
    measurement: "flat_insulation_squares",
  },
  {
    key: "flat_deck",
    label: "Flat deck products (roof only)",
    measurement: "flat_deck_squares",
  },
  /*
   * The two halves of a flat roof's perimeter, which must never overlap.
   *
   * Termination goes on the open edge; coping goes on the wall. Putting both
   * on `flat_perimeter` would buy drip edge for the parapets and coping for
   * the eaves.
   */
  {
    key: "open_edge",
    label: "Open edge products (eave + rake)",
    measurement: "open_edge_lf",
  },
  { key: "open_edge_ea", label: "Open edge products ea", measurement: null },
  { key: "wall", label: "Parapet wall products", measurement: "wall_lf" },
  { key: "wall_ea", label: "Parapet wall products ea", measurement: null },
  {
    key: "flat_perimeter",
    label: "Flat area perimeter products",
    measurement: "flat_perimeter_lf",
  },
  {
    key: "steep_mid",
    label: "Steep charge 8/12 - 12/12",
    measurement: "steep_mid_squares",
  },
  {
    key: "steep_high",
    label: "Steep charge 13/12 and greater",
    measurement: "steep_high_squares",
  },
  {
    key: "tearoff",
    label: "Tear-off and disposal",
    measurement: "tearoff_squares",
  },
  /*
   * The removal labour for every layer after the first. The base labour pays
   * for one layer; this pays for the rest, by the square and with no waste —
   * a crew strips the roof that is there, not ten percent more of it.
   */
  {
    key: "extra_layers",
    label: "Extra layers (each layer after the first)",
    measurement: "extra_layer_squares",
    noWaste: true,
  },

  ...WALL_SLOTS,
  ...DECK_SLOTS,
];

/** The slots a template of this surface is built from. */
export function slotsForSurface(surface: SlotSurface): RoofSlot[] {
  return ROOF_SLOTS.filter((slot) => (slot.surface ?? "roof") === surface);
}

export const roofSlot = (key: string | null | undefined): RoofSlot | null =>
  ROOF_SLOTS.find((s) => s.key === key) ?? null;

/**
 * Where a generated line belongs.
 *
 * Labour and disposal go on the work order — the crew is paid for both. The
 * supplier's own delivery and fuel charges stay on the material order, because
 * that is what the supplier invoices. Everything else is material.
 */
export type LineTarget = "material_order" | "work_order";

const WORK_ORDER_CATEGORIES: readonly string[] = ["labor", "disposal"];

/**
 * Where a line goes, from what the catalogue says it is.
 *
 * `kind` is the product's own answer and it wins. Category is how a product is
 * filed for browsing, and two dozen labour products are filed under the trade
 * they belong to — Standing Seam Labor under Metal Roofing, CUT and INSTALL
 * RIDGE VENT under Hip & Ridge, TPO labour under Flat Roofing. Routing on
 * category alone billed every one of them to the supplier, which is the crew's
 * money on the wrong document and a margin that looks fine because the total
 * is unchanged.
 *
 * Category is still the fallback: rows imported from the prototype have no
 * kind, and "Labor" or "Disposal" there means what it says.
 */
export function targetForCategory(
  category: string | null | undefined,
  kind?: string | null,
): LineTarget {
  const k = (kind ?? "").trim().toLowerCase();
  if (k === "labor") return "work_order";
  if (k === "material") return "material_order";

  const c = (category ?? "").trim().toLowerCase();
  return WORK_ORDER_CATEGORIES.includes(c) ? "work_order" : "material_order";
}

/**
 * Where a labour line sits on the work order.
 *
 * A work order is read by a crew chief standing in a driveway, and what they
 * are doing is putting a roof on in a fixed order. So the document runs in that
 * order — the order of the day, not the order the money is worked out in.
 *
 * Jonathon, 2026-08-13: "the work order line items should be in order of how a
 * roof install goes: dump trailer, decking labor, drip edge, starter, shingle
 * base labor, chimney cricket, chimney reflash, hip and ridge."
 *
 * That moved disposal from the bottom to the top, which is the whole point: the
 * trailer is dropped before anyone climbs a ladder. The steep charges are not
 * in his list because they are not a step — they are what the base labour costs
 * on a steep roof — so they sit immediately under the base labour they add to,
 * which is where they already were relative to it.
 *
 * Matched on the name the product already carries, the same way the material
 * sequence is (`SHINGLE_INSTALL_ORDER` below). Nothing is renamed or
 * re-categorised to make the sort work. The slot is the fallback, because a
 * template line whose product has an unusual name still knows what part of the
 * roof it covers.
 */
const WORK_ORDER_INSTALL_ORDER: readonly {
  rank: number;
  tone: WorkStepTone;
  match: (name: string) => boolean;
}[] = [
  // The trailer is dropped before anybody climbs a ladder.
  {
    rank: 10,
    tone: "disposal",
    match: (n) => /dump\s*trailer|disposal|haul|debris|tear.?off/.test(n),
  },
  {
    rank: 20,
    tone: "deck",
    match: (n) => /deck|\bosb\b|plywood|sheath/.test(n),
  },
  { rank: 30, tone: "edge", match: (n) => /drip\s*edge/.test(n) },
  { rank: 40, tone: "edge", match: (n) => /starter|pro.?start/.test(n) },
  // Before the generic /shingle/ rule below: "8/12-12/12 STEEP PITCH LABOR"
  // does not say shingle, but "STEEP PITCH SHINGLE LABOR" would, and it is a
  // steep charge either way.
  { rank: 55, tone: "field", match: (n) => /steep/.test(n) },
  {
    rank: 50,
    tone: "field",
    match: (n) => /shingle|base\s*labor|install\s*labor|\bfield\b/.test(n),
  },
  { rank: 60, tone: "chimney", match: (n) => /cricket|saddle/.test(n) },
  {
    rank: 70,
    tone: "chimney",
    match: (n) => /reflash|chimney|counter\s*flash|\bflash/.test(n),
  },
  {
    rank: 80,
    tone: "ridge",
    match: (n) =>
      /hip\s*(&|and)?\s*ridge|ridge\s*cap|seal.?a.?ridge|ridge/.test(n),
  },
];

/** Which band a work order line belongs to, for colour on screen. */
export type WorkStepTone =
  "disposal" | "deck" | "edge" | "field" | "chimney" | "ridge" | "other";

/**
 * The slot's answer, for a line whose name says nothing recognisable.
 *
 * Deliberately the same numbers as the name rules above, so the two agree
 * rather than interleaving.
 */
const SLOT_FALLBACK: Record<string, { rank: number; tone: WorkStepTone }> = {
  tearoff: { rank: 10, tone: "disposal" },
  extra_layers: { rank: 10, tone: "disposal" },
  field: { rank: 50, tone: "field" },
  steep_mid: { rank: 55, tone: "field" },
  steep_high: { rank: 56, tone: "field" },
  hip_ridge: { rank: 80, tone: "ridge" },
  ridge: { rank: 80, tone: "ridge" },
  eave: { rank: 30, tone: "edge" },
  rake: { rank: 30, tone: "edge" },
  flashing: { rank: 70, tone: "chimney" },
  step_flashing: { rank: 70, tone: "chimney" },
  // A siding job runs: tear-off, wrap the house, hang the siding, then the
  // trim work — openings, soffit, fascia, frieze.
  siding_removal: { rank: 10, tone: "disposal" },
  house_wrap: { rank: 20, tone: "deck" },
  siding: { rank: 50, tone: "field" },
  openings: { rank: 60, tone: "other" },
  soffit: { rank: 62, tone: "other" },
  fascia: { rank: 64, tone: "other" },
  frieze: { rank: 66, tone: "other" },
  // A deck is one line of labour and the steps beyond the first.
  deck_labor: { rank: 50, tone: "field" },
};

/**
 * Anything the sequence does not name sits between the roof and the ridge cap
 * rather than at either end, where it would look deliberate.
 */
const UNSEQUENCED_WORK_RANK = 75;

export function workOrderStep(
  name: string | null | undefined,
  slotKey?: string | null,
): { rank: number; tone: WorkStepTone } {
  const n = (name ?? "").toLowerCase();
  for (const step of WORK_ORDER_INSTALL_ORDER) {
    if (step.match(n)) return { rank: step.rank, tone: step.tone };
  }
  const bySlot = slotKey ? SLOT_FALLBACK[slotKey] : undefined;
  return bySlot ?? { rank: UNSEQUENCED_WORK_RANK, tone: "other" };
}

export function workOrderRank(slotKey: string, name?: string | null): number {
  return workOrderStep(name, slotKey).rank;
}

/**
 * The order a shingle roof's materials go on, for reading the order against the
 * roof.
 *
 * Jonathon, 2026-08-07: "the only reason i am doing this is so mentally we can
 * go through the materials on how they're installed to make sure we're ordering
 * everything." So this is a checklist aid, not a pricing rule — it moves lines
 * up and down and changes no quantity, no name and no product.
 *
 * Matched on what the product already is. Nothing is renamed or re-categorised
 * to make the sort work: two of these steps are the same catalogue category
 * ("Fasteners" holds both cap nails and roofing nails) and are told apart by
 * the name that is already on them.
 *
 * Metal roofs are left alone — they have their own sequence, set per template
 * line by the 20260731740000 migration, and it is a different roof.
 */
const SHINGLE_INSTALL_ORDER: readonly {
  rank: number;
  match: (name: string, category: string) => boolean;
}[] = [
  {
    rank: 10,
    match: (n, c) =>
      /\bosb\b|deck.?over|\bdecking\b/.test(n) || c === "lumber & decking",
  },
  { rank: 20, match: (n) => /drip\s*edge/.test(n) },
  {
    rank: 30,
    match: (n, c) =>
      /ice\s*(&|and)?\s*water/.test(n) || c === "ice & water shield",
  },
  {
    rank: 40,
    match: (n, c) =>
      /underlayment|felt\s*buster|synthetic|\bfelt\b/.test(n) ||
      c === "underlayment",
  },
  // Before shingles: the cap nail holds the underlayment down.
  { rank: 50, match: (n) => /cap\s*nail/.test(n) },
  // "GAF ProStart" is the starter this company buys, and /starter/ does not
  // match it — the word is "prostart". It was falling to the bottom of the
  // order, below the delivery charge.
  {
    rank: 60,
    match: (n, c) => /starter|pro.?start/.test(n) || c === "starter",
  },
  { rank: 70, match: (n, c) => /shingle/.test(n) || c === "shingles" },
  // Every other fastener. Cap nails have already been taken above.
  {
    rank: 80,
    match: (n, c) => /nail|screw|staple/.test(n) || c === "fasteners",
  },
  {
    rank: 90,
    match: (n, c) => /pipe\s*boot|boot\b/.test(n) || c === "pipe boots",
  },
  // Before the sealants, because "GAF Liberty Flashing Cement" would otherwise
  // match on `cement` and sort a hundred places above the roof it belongs to.
  // Jonathon, 2026-08-08: "both liberty products need to come right after the
  // rolled roofing products."
  { rank: 135, match: (n) => /\bliberty\b/.test(n) },
  {
    rank: 100,
    match: (n, c) =>
      /sealant|caulk|cement|accessor/.test(n) ||
      c === "sealants" ||
      c === "accessories",
  },
  {
    rank: 110,
    match: (n, c) =>
      /vent\b|vents\b|ventilation|turbine|attic/.test(n) || c === "ventilation",
  },
  {
    rank: 120,
    match: (n, c) =>
      /ridge\s*cap|hip\s*(&|and)\s*ridge|seal.?a.?ridge/.test(n) ||
      c === "hip & ridge",
  },
  {
    rank: 130,
    match: (n) =>
      /rolled\s*roofing|roll\s*roofing|modified\s*bitumen|\btorch\b/.test(n),
  },
  {
    rank: 140,
    match: (n, c) =>
      /flashing|counter\s*flash|apron/.test(n) || c === "flashing",
  },
  { rank: 150, match: (n) => /delivery|freight|fuel\s*surcharge/.test(n) },
];

/** Anything the sequence does not name sits below the delivery charge, which is
 *  where line items get added by hand. */
export const MATERIAL_UNSEQUENCED_RANK = 900;

/**
 * How much room each step gets on the finished order.
 *
 * A step is not one product — a roof can carry two shingles, or a counter
 * flashing beside an apron. Ranked alone they would tie on `sort_order` and the
 * order within the step would be whatever the database happened to return that
 * time. So the rank is multiplied up and the line's position in the template
 * fills the gap: the sequence is kept, ties break the way the template was
 * written, and the same roof reads the same way every time.
 *
 * Wide enough that the tie-breaker can never reach the next step — no material
 * order in this business runs to a thousand lines.
 */
export const INSTALL_STEP_SPAN = 1000;

export function shingleInstallRank(
  name: string | null | undefined,
  category?: string | null,
): number {
  const n = (name ?? "").toLowerCase();
  const c = (category ?? "").toLowerCase();
  for (const step of SHINGLE_INSTALL_ORDER) {
    if (step.match(n, c)) return step.rank;
  }
  return MATERIAL_UNSEQUENCED_RANK;
}
